import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  DialogTitle,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../../constants/analyticsMeta';
import { useCustomerInfo, useIsTestDriveUser, useQalert } from '../../../utility/services/customHooks';
import QuattrLoader from '../../shared/QuattrLoader';
import ApiService from '../../../api/restApi';

import Router from 'next/router';
import { v4 as uuid } from 'uuid';
import { delayTimer } from '../../../utility/cr-common';
import { DEFAULT_INTERNAL_LINKING_FILTERS_OBJ } from '../../../constants/formMeta/growthTrends';
import { validateIlModuleName } from '../../../utility/common';
import { API_STATUS, TEMP_IL_UNIQUE_NAME } from '../../../constants/common';

// Import TemplateSelectionGallery and buildTemplateSettingsForDB for the new flow
import { TemplateSelectionGallery, TemplateType, buildTemplateSettingsForDB } from './TemplateConfigurator';

const useLoginCustomerInfo = () => {
  const isTestDriveUser = useIsTestDriveUser();
  const customerInfoFromState = useSelector((state) => state.root.customerInfo);
  const customerInfo = useCustomerInfo(customerInfoFromState);

  if (!isTestDriveUser) {
    return customerInfo;
  }

  return undefined;
};

const AddNewLink = ({ isNewInternalLinking = false }) => {
  const [openAddNewPage, setOpenAddNewPage] = useState(false);
  const [loader, setLoader] = useState(false);

  const loginCustomerInfo = useLoginCustomerInfo();

  const [, setSnackBar] = useQalert();

  const hideLoader = () => {
    setTimeout(() => {
      setLoader(false);
    }, 500);
  };

  const [linkName, setLinkName] = useState('');
  const [addNewLinkLoading, setAddNewLinkLoading] = useState(false);

  // Updated: Use TemplateSelectionGallery instead of simple dialog
  const [isOpenAutoNomousILForm, setIsOpenAutoNomousILForm] = useState(false);
  const [moduleNameError, setModuleNameError] = useState('');

  useEffect(() => {
    setIsOpenAutoNomousILForm(false);
  }, []);

  const currentIlListDetails = useSelector((state) => state?.root?.autoNomousInternalLinkingDetails) as {
    status: string;
    list: any[];
  };

  const setTempLinkingNameHandler = useCallback(
    (reset = false) => {
      if (currentIlListDetails?.status === API_STATUS.SUCCESS && currentIlListDetails?.list) {
        const currentUsedNamesByUser: any[] = [];
        for (const ilData of currentIlListDetails?.list || []) {
          currentUsedNamesByUser?.push(
            ilData?.['Overall Metrics']?.currentUsedName?.publishedName?.toLowerCase(),
            ilData?.['Overall Metrics']?.currentUsedName?.draftName?.toLowerCase()
          );
        }

        const randomNumber = Math.floor(Math.random() * 196);
        setLinkName(reset ? '' : TEMP_IL_UNIQUE_NAME?.[randomNumber] || TEMP_IL_UNIQUE_NAME[0]);
      }
    },
    [currentIlListDetails?.list, currentIlListDetails?.status]
  );

  const onCloseHandler = useCallback(() => {
    setModuleNameError('');
    setIsOpenAutoNomousILForm(false);
    setLinkName('');
  }, []);

  const trackAnalyticsEventCustom = useCallback(
    ({ eventName, eventFor = '', label = '', extraPayload = {} }) => {
      trackAnalyticsEvent({
        eventFor: eventFor,
        buttonText: ``,
        eventName: eventName,
        payload: {
          label: label,
          event_name: eventName,
          component_name: isNewInternalLinking
            ? COMPONENT_NAMES.autonomous_internal_linking
            : COMPONENT_NAMES.internal_linking_recommendations,
          screen_name: isNewInternalLinking
            ? SCREEN_NAMES.autonomous_internal_linking
            : SCREEN_NAMES.internal_linking_recommendations,
          ...(extraPayload || {}),
        },
      });
    },
    [isNewInternalLinking]
  );

  const addNewPageBoxHandler = useCallback(() => {
    const eventTrackDetails = {
      eventName: isNewInternalLinking ? 'Click - Add Linking Module' : 'Add Linking Module',
      eventFor: 'button',
      label: isNewInternalLinking ? 'Click - Add Linking Module' : 'Add Linking Module',
    };
    trackAnalyticsEventCustom(eventTrackDetails);
    if (isNewInternalLinking) {
      // Set auto-generated name and open template selection gallery
      setTempLinkingNameHandler();
      setModuleNameError('');
      setIsOpenAutoNomousILForm(true);
    } else {
      setOpenAddNewPage(!openAddNewPage);
      setLoader(true);
    }
  }, [isNewInternalLinking, openAddNewPage, setTempLinkingNameHandler, trackAnalyticsEventCustom]);

  // Updated handler for TemplateSelectionGallery
  const handleTemplateSelectionConfirm = useCallback(
    async (templateId: TemplateType | null, moduleName?: string) => {
      if (!moduleName) {
        setModuleNameError('Module name is required');
        return;
      }

      const moduleId = uuid();
      const eventTrackDetails = {
        eventName: 'Linking Module Added Successfully',
        extraPayload: {
          module_name: moduleName,
          module_id: moduleId,
          template_id: templateId || 'none',
        },
      };

      try {
        trackAnalyticsEventCustom({ ...eventTrackDetails, eventName: 'Click - Add New Link' });
        const invalidNameErrorMessage = validateIlModuleName(moduleName);
        setModuleNameError(invalidNameErrorMessage);
        if (invalidNameErrorMessage) {
          return;
        }
        setAddNewLinkLoading(true);
        const payload = {
          module_id: moduleId,
          module_name: moduleName,
          module_details: {
            ...(DEFAULT_INTERNAL_LINKING_FILTERS_OBJ || {}),
          },
          isAlreadySampleOutputExist: false,
          validateName: true,
          subCustomerPath: '',
          isNewInternalLinking: true,
        };
        payload.module_details['Overall Metrics'].Module_Name = moduleName;

        // Save template settings if a template was selected
        if (templateId) {
          const templateSettings = buildTemplateSettingsForDB(templateId, {});
          if (templateSettings) {
            payload.module_details['Overall Metrics'].Template_Settings = templateSettings.Template_Settings;
          }
        }

        const result = await ApiService.post(
          `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/discoverability/internal-linking-recommendation/save-module-details`,
          payload
        );
        if (result) {
          trackAnalyticsEventCustom(eventTrackDetails);
          const path = `/app/discoverability/autonomous-internal-linking/[id]`;
          const as = `/app/discoverability/autonomous-internal-linking/${moduleId}`;
          Router.push(path, as);
        } else {
          const failedTrackingDetails = {
            ...eventTrackDetails,
            eventName: 'Failed To Add Linking Module',
          };
          trackAnalyticsEventCustom(failedTrackingDetails);
          console.error('ResultError :: AddNewLink :: error while creating new linking', result);
          setAddNewLinkLoading(false);
        }
      } catch (error: any) {
        console.error('Error :: AddNewLink :: error while creating new linking', error);
        const obj = error?.response?.data?.data || error?.response?.data?.message;
        if (typeof obj === 'object' && obj?.module_id) {
          setSnackBar({ type: 'error', text: 'Linking Module Name already exists.', open: true });
        } else {
          setSnackBar({
            type: 'error',
            text: 'Oops, Something went wrong !!',
            open: true,
          });
        }
        setAddNewLinkLoading(false);
        const failedTrackingDetails = {
          ...eventTrackDetails,
          eventName: 'Failed To Add Linking Module',
        };
        trackAnalyticsEventCustom(failedTrackingDetails);
      }
    },
    [setSnackBar, trackAnalyticsEventCustom]
  );

  return (
    <div className="add-new-link-wrapper">
      {(loginCustomerInfo?.additional_user_info?.internalLinkingForm && !isNewInternalLinking) ||
      (isNewInternalLinking && currentIlListDetails?.status === API_STATUS.SUCCESS) ? (
        <Button
          className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
          data-testid="add-new-link-button"
          variant="contained"
          color="primary"
          onClick={() => {
            addNewPageBoxHandler();
          }}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          + Add Linking Module
        </Button>
      ) : null}
      <Dialog
        PaperProps={{
          style: {
            width: '80%',
            minWidth: 300,
            borderRadius: '4px',
            height: '90%',
          },
        }}
        onClose={addNewPageBoxHandler}
        aria-labelledby="simple-dialog-title"
        className={''}
        maxWidth="sm"
        open={openAddNewPage}
        data-testid="addNewKeywordDlgBoxWrapper"
      >
        <Close
          onClick={addNewPageBoxHandler}
          style={{ position: 'absolute', right: '20px', top: '5px', cursor: 'pointer' }}
        />

        <iframe
          sandbox="allow-forms allow-scripts allow-same-origin"
          src={loginCustomerInfo?.additional_user_info?.internalLinkingForm}
          title="airtable-form"
          width="100%"
          height="100%"
          onLoad={hideLoader}
          style={{ border: 'none' }}
        ></iframe>
        {loader ? <QuattrLoader isTransparent /> : null}
      </Dialog>

      {/* Updated: Use TemplateSelectionGallery instead of simple dialog */}
      <TemplateSelectionGallery
        open={isOpenAutoNomousILForm}
        onClose={onCloseHandler}
        onSelect={handleTemplateSelectionConfirm}
        showModuleName={true}
        moduleName={linkName}
        onModuleNameChange={setLinkName}
        moduleNameError={moduleNameError}
        allowSkip={true}
        title="Create New Linking Module"
        confirmButtonText={addNewLinkLoading ? 'Creating...' : 'Create Module'}
      />
    </div>
  );
};

export default React.memo(AddNewLink);
